import React, { useCallback, useState } from 'react';

import { useMutation } from '@apollo/client';
import { stringify } from '@stratumn/canonicaljson';
import to from 'await-to-js';
import compose from 'lodash.flowright';
import {
  Archive,
  Code,
  History,
  Settings,
  TableColumnsSplit,
  UsersRound
} from 'lucide-react';
import Path from 'path-to-regexp';
import { withRouter } from 'react-router-dom';

import type { FastWorkflowQueryQuery } from '@/apollo/graphql';
import { withError } from '@/components/errorBoundary/errorBoundary';
import { Navbar } from '@/components/layouts';
import { notify } from '@/components/toast';
import { EffectsViewer } from '@/components/ui/effectsViewer/effectsViewer';
import { JsonEditor } from '@/components/ui/utils/jsonEditor/jsonEditor';
import { env } from '@/constant/env';
import {
  ROUTE_WORKFLOW_BATCH_ACTIONS,
  ROUTE_WORKFLOW_GROUPS,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';
import { type UserContextType, withUser } from '@/contexts';
import { UPDATE_WF_CONFIG_MUTATION } from '@/gql/queries/fast-workflow-overview.gql';
import {
  TYPE_NEW_WORKFLOW_CONFIG_PAYLOAD,
  TYPE_WORKFLOW,
  TYPE_WORKFLOW_CONFIG
} from '@/gql/types';
import { useUpdateArchiveCondition } from '@/hooks/useUpdateArchiveCondition';
import { hasElevatedRights } from '@/utils/privileges';
import { buildOverviewInfoLinks } from './overviewInfoLinks';

type PartialWorkflowConfig = {
  initState?: unknown;
  initActions?: unknown;
  stateSchema?: unknown;
  actions?: unknown;
  transitions?: unknown;
};

type WorkflowOverviewHeaderProps = {
  match: { params: { id: string } };
  history: { push: (path: string) => void };
  // Null until PrivateRoute mounts UserContext; `me` undefined until the profile
  // query resolves. Typed honestly so callers cannot assume it is populated.
  user: UserContextType | null;
  workflow: FastWorkflowQueryQuery['workflowByRowId'];
  isLoading: boolean;
  // Which view is on screen. The batch entry is a toggle: on the overview it
  // offers "Batch actions"; on the history page it flips to a highlighted
  // "Workflow overview" so the way back is obvious.
  activeView?: 'overview' | 'batchActions';
};

const Comp = React.memo((props: WorkflowOverviewHeaderProps) => {
  const {
    match,
    history,
    user,
    workflow,
    isLoading,
    activeView = 'overview'
  } = props;

  const [updateWorkflowConfigMutation] = useMutation(UPDATE_WF_CONFIG_MUTATION);
  const handleArchiveConditionUpdate = useUpdateArchiveCondition(
    workflow?.config?.rowId
  );

  const [showWorkflowConfigEditor, setShowWorkflowConfigEditor] =
    useState(false);
  const toggleShowWorkflowConfigEditor = useCallback(
    () => setShowWorkflowConfigEditor(!showWorkflowConfigEditor),
    [showWorkflowConfigEditor, setShowWorkflowConfigEditor]
  );

  const [showEffectsViewer, setShowEffectsViewer] = useState(false);
  const toggleShowEffectsViewer = useCallback(
    () => setShowEffectsViewer(!showEffectsViewer),
    [showEffectsViewer]
  );

  const [showArchiveConditionEditor, setShowArchiveConditionEditor] =
    useState(false);
  const toggleShowArchiveConditionEditor = useCallback(
    () => setShowArchiveConditionEditor(!showArchiveConditionEditor),
    [showArchiveConditionEditor]
  );

  const handleWorkflowConfigUpdate = useCallback(
    async (partialConfig: PartialWorkflowConfig) => {
      if (!workflow?.config) return;

      const { rowId: workflowRowId } = workflow;
      const {
        rowId: workflowConfigId,
        overview,
        initState,
        initActions,
        stateSchema,
        actions,
        transitions
      } = workflow.config;

      // Merge current config with partial update
      const newConfig = {
        initState: partialConfig.initState ?? initState,
        initActions: partialConfig.initActions ?? initActions,
        stateSchema: partialConfig.stateSchema ?? stateSchema,
        actions: partialConfig.actions ?? actions,
        transitions: partialConfig.transitions ?? transitions
      };

      const promise = updateWorkflowConfigMutation({
        variables: {
          workflowRowId,
          newInitState: newConfig.initState,
          newInitActions: newConfig.initActions,
          newStateSchema: newConfig.stateSchema,
          newActions: newConfig.actions,
          newTransitions: newConfig.transitions
        },
        optimisticResponse: {
          newWorkflowConfig: {
            workflow: {
              rowId: workflowRowId,
              config: {
                rowId: workflowConfigId,
                overview,
                ...newConfig,
                __typename: TYPE_WORKFLOW_CONFIG
              },
              __typename: TYPE_WORKFLOW
            },
            __typename: TYPE_NEW_WORKFLOW_CONFIG_PAYLOAD
          }
        }
      });

      notify.promise(promise, {
        loading: '🛠️ Updating the workflow configuration...',
        success: 'The workflow configuration was updated',
        error: data => {
          console.error('Workflow configuration update error: ', data);
          return 'Something went wrong during the workflow configuration update...';
        }
      });

      await to(promise);
    },
    [workflow, updateWorkflowConfigMutation]
  );

  const goToGroupsSettings = useCallback(
    () =>
      history.push(
        Path.compile(ROUTE_WORKFLOW_GROUPS)({
          id: match.params.id
        })
      ),
    [history, match]
  );

  const goToBatchActions = useCallback(
    () =>
      history.push(
        Path.compile(ROUTE_WORKFLOW_BATCH_ACTIONS)({
          id: match.params.id
        })
      ),
    [history, match]
  );

  const goToWorkflowOverview = useCallback(
    () =>
      history.push(
        Path.compile(ROUTE_WORKFLOW_OVERVIEW)({
          id: match.params.id
        })
      ),
    [history, match]
  );

  // `isLoading` tracks the WORKFLOW query, not the profile, so `user` and
  // `user.me` can both still be empty here.
  const isPrivileged = hasElevatedRights(user?.me);
  // The config entries act on the workflow definition, which the batch history
  // doesn't show — they'd only be noise next to its own filters.
  const showWorkflowConfigActions = isPrivileged && activeView === 'overview';

  let workflowConfigStr: string | undefined;
  let archiveConditionStr: string | undefined;

  if (!isLoading && workflow?.config) {
    const {
      initState,
      initActions,
      stateSchema,
      actions,
      transitions,
      archiveCondition
    } = workflow.config;
    workflowConfigStr = stringify(
      {
        initState,
        initActions,
        stateSchema,
        actions,
        transitions
      },
      null,
      2
    );
    archiveConditionStr =
      archiveCondition === undefined || archiveCondition === null
        ? ''
        : stringify(archiveCondition, null, 2);
  }

  const configHeader = {
    loading: isLoading,
    bottomLevel: {
      workflowPage: true,
      actions: {
        links: [
          ...(showWorkflowConfigActions
            ? [
                {
                  icon: <UsersRound />,
                  label: 'Manage groups',
                  onClick: goToGroupsSettings
                },
                {
                  icon: <Settings />,
                  label: 'Workflow configuration',
                  onClick: toggleShowWorkflowConfigEditor
                },
                {
                  icon: <Code />,
                  label: 'Show effects',
                  onClick: toggleShowEffectsViewer
                },
                ...(env.FEATURE_FLAGS.USE_ARCHIVAGE_FEATURE
                  ? [
                      {
                        icon: <Archive strokeWidth={2} />,
                        label: 'Archive condition',
                        onClick: toggleShowArchiveConditionEditor
                      }
                    ]
                  : [])
              ]
            : []),
          // Outside the superuser block: batch actions are for every user.
          ...(env.FEATURE_FLAGS.USE_BATCH_ACTION
            ? [
                activeView === 'batchActions'
                  ? {
                      icon: <TableColumnsSplit />,
                      label: 'Workflow overview',
                      onClick: goToWorkflowOverview,
                      className: 'text-primary hover:text-primary'
                    }
                  : {
                      icon: <History />,
                      label: 'Batch actions',
                      onClick: goToBatchActions
                    }
              ]
            : [])
        ]
      }
    }
  };

  if (workflow) {
    // @ts-expect-error I dont know where infoContext is coming from
    configHeader.bottomLevel.infoContext = {
      links: buildOverviewInfoLinks(workflow?.name)
    };
  }

  return (
    <>
      <Navbar config={configHeader} />

      {isPrivileged && showWorkflowConfigEditor && (
        <JsonEditor
          jsonString={workflowConfigStr ?? ''}
          onClose={toggleShowWorkflowConfigEditor}
          onSubmit={async jsonStr => {
            const parsed = jsonStr ? JSON.parse(jsonStr) : {};
            await handleWorkflowConfigUpdate(parsed);
          }}
          title="Workflow configuration"
        />
      )}

      {isPrivileged && showEffectsViewer && (
        <EffectsViewer
          actions={workflow.config?.actions as Record<string, unknown>}
          onClose={toggleShowEffectsViewer}
          onSubmit={handleWorkflowConfigUpdate}
        />
      )}

      {env.FEATURE_FLAGS.USE_ARCHIVAGE_FEATURE &&
        isPrivileged &&
        showArchiveConditionEditor && (
          <JsonEditor
            jsonString={archiveConditionStr ?? ''}
            onClose={toggleShowArchiveConditionEditor}
            onSubmit={async jsonStr => {
              await handleArchiveConditionUpdate(
                jsonStr ? JSON.parse(jsonStr) : null
              );
            }}
            title="Archive condition"
          />
        )}
    </>
  );
});

export const FastWorkflowOverviewHeader = compose(
  withRouter,
  withUser,
  withError
)(Comp);
